import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import {
    validatePassword,
    validatePasswordMatch,
} from '../../../_helpers/validate';
import InputSuccessMessage from '../../inputMessages/inputSuccessMessage/inputSuccessMessage';
import InputErrorMessage from '../../inputMessages/inputErrorMessage/inputErrorMessage';
import PasswordStrengthChecker from '../../passwordStrengthChecker/passwordStrengthChecker';
import PasswordValidator from '../../passwordValidator/passwordValidator';
import { fetchWrapper } from '../../../_helpers';
import SuccessMessage from '../../successMessage/successMessage';
import ErrorMessage from '../../errorMessage/errorMessage';

const Password = ({ userData }) => {
    // Defaults
    const baseUrl = `${process.env.REACT_APP_API_URL}`;

    // State
    const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [passStrData, setPassStrData] = useState(null);
    const [passLengthErr, setPassLengthErr] = useState(null);
    const [userPassword, setUserPassword] = useState(undefined);
    const [validPasswordMatch, setValidPasswordMatch] = useState(null);
    const [userPasswordConfirm, setUserPasswordConfirm] = useState(undefined);
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
    const [errorPasswordChange, setErrorPasswordChange] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // UseEffects
    useEffect(() => {
        setPasswordChangeSuccess(false);
        setUserPassword(undefined);
        setPassStrData(null);
        setErrorPasswordChange(null);
    }, []);

    // form validation rules
    const validationSchema = Yup.object().shape({
        currentPassword: Yup.string().required('Current password is required'),
        password: Yup.string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters')
            .matches(
                /(?=.*[a-z])(?=.*[A-Z])|(?=.*[0-9])|(?=.*[!@#$%^&*])/,
                'Password must contain at least two of the following: uppercase letters, lowercase letters, numbers, special characters'
            ),
        confirmPassword: Yup.string().oneOf(
            [Yup.ref('password'), null],
            'Passwords must match'
        ),
    });

    const formOptions = { resolver: yupResolver(validationSchema) };
    const { register, handleSubmit, clearErrors } = useForm(formOptions);

    const toggleCurrentPasswordVisibility = () => {
        setCurrentPasswordVisible(!currentPasswordVisible);
    };

    const toggleNewPasswordVisibility = () => {
        setNewPasswordVisible(!newPasswordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    const validateInputs = (e) => {
        const { name, value } = e.target;
        let valid;

        switch (name) {
            case 'password':
                const strengthCheckerData = validatePassword(value);
                setPassStrData(strengthCheckerData);
                setUserPassword(value);

                if (
                    userPasswordConfirm !== null &&
                    userPasswordConfirm !== undefined &&
                    userPasswordConfirm !== ''
                ) {
                    valid = validatePasswordMatch(userPasswordConfirm, value);
                    valid
                        ? setValidPasswordMatch(true)
                        : setValidPasswordMatch(false);
                }
                break;
            case 'confirmPassword':
                valid = validatePasswordMatch(userPassword, value);
                setValidPasswordMatch(valid);
                setUserPasswordConfirm(value);
                break;
            default:
                break;
        }
    };

    const onSubmit = async ({ currentPassword, password }) => {
        if (passStrData && passStrData.contains.length !== 4) {
            setPassLengthErr(true);
            return null;
        }

        setIsLoading(true);
        setPasswordChangeSuccess(false);
        setErrorPasswordChange(null);
        setPassLengthErr(false);

        const urlParams = new URLSearchParams(window.location.search);
        const session_state = urlParams.get('session_state');

        try {
            await fetchWrapper.put(`${baseUrl}/profile/update-user-password`, {
                email: userData.email,
                currentPassword: currentPassword,
                password: password,
                sessionId: session_state,
            });

            setIsLoading(false);
            setPasswordChangeSuccess(true);
        } catch (error) {
            setIsLoading(false);
            setErrorPasswordChange(true);
        }
    };

    return (
        <div
            style={{ boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}
            className='card mb-5 no-border m-md-3 pt-4 pb-5 d-flex align-items-center'
        >
            <div className='card-body'>
                <h2 className='card-header no-border text-center mb-5'>
                    Change Password
                </h2>
                {passwordChangeSuccess ? (
                    <div className='mb-3'>
                        <SuccessMessage message='YOUR PASSWORD HAS BEEN UPDATED SUCCESSFULLY!' />
                    </div>
                ) : null}
                {errorPasswordChange ? (
                    <div className='mb-3'>
                        <ErrorMessage message='COULD NOT UPDATE YOUR PASSWORD' />
                    </div>
                ) : null}
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* CURRENT PASSWORD */}
                    <div className='align-items-center mb-4 edit-profile-inputs'>
                        <label className='form-label fw-bold'>
                            Current Password
                        </label>
                        <div className='password-input-container'>
                            <input
                                name='currentPassword'
                                type={
                                    currentPasswordVisible ? 'text' : 'password'
                                }
                                {...register('currentPassword')}
                                className='form-control'
                            />
                            <span
                                className='password-toggle-icon'
                                onClick={toggleCurrentPasswordVisibility}
                            >
                                {currentPasswordVisible ? (
                                    <FaEyeSlash />
                                ) : (
                                    <FaEye />
                                )}
                            </span>
                        </div>
                    </div>

                    {/* PASSWORD */}
                    <div className='align-items-center mb-4 edit-profile-inputs'>
                        <label className='form-label fw-bold'>Password</label>
                        <div className='password-input-container'>
                            <input
                                name='password'
                                type={newPasswordVisible ? 'text' : 'password'}
                                {...register('password')}
                                className='form-control'
                                onChange={(e) => {
                                    clearErrors('password');
                                    setPassLengthErr(false);
                                    if (e.target.value.includes(' ')) {
                                        return null;
                                    } else {
                                        validateInputs(e);
                                    }
                                }}
                                value={userPassword}
                            />
                            <span
                                className='password-toggle-icon'
                                onClick={toggleNewPasswordVisibility}
                            >
                                {newPasswordVisible ? (
                                    <FaEyeSlash />
                                ) : (
                                    <FaEye />
                                )}
                            </span>
                        </div>
                    </div>

                    {/* CONFIRM PASSWORD */}
                    <div className='align-items-center mb-4 edit-profile-inputs'>
                        <label className='form-label fw-bold'>
                            Confirm Password
                        </label>
                        <div className='password-input-container'>
                            <input
                                name='confirmPassword'
                                type={
                                    confirmPasswordVisible ? 'text' : 'password'
                                }
                                {...register('confirmPassword')}
                                className='form-control'
                                onChange={(e) => {
                                    clearErrors('confirmPassword');
                                    if (e.target.value.includes(' ')) {
                                        return null;
                                    } else {
                                        validateInputs(e);
                                    }
                                }}
                                value={userPasswordConfirm}
                            />
                            <span
                                className='password-toggle-icon'
                                onClick={toggleConfirmPasswordVisibility}
                            >
                                {confirmPasswordVisible ? (
                                    <FaEyeSlash />
                                ) : (
                                    <FaEye />
                                )}
                            </span>
                        </div>
                    </div>
                    <div className='mt-3'>
                        {validPasswordMatch === true ? (
                            <InputSuccessMessage
                                message={'The passwords are matching!'}
                            />
                        ) : null}
                        {validPasswordMatch === false ? (
                            <InputErrorMessage
                                message={'The passwords do not match!'}
                            />
                        ) : null}
                        {passLengthErr === true ? (
                            <InputErrorMessage
                                message={
                                    'Password has not fulfilled the requirements!'
                                }
                            />
                        ) : null}
                    </div>
                    <div className='mb-3'>
                        {/* Password Strength Checker */}
                        <PasswordStrengthChecker passwordData={passStrData} />
                    </div>
                    <div className='mb-3'>
                        {/* Password Validator */}
                        <PasswordValidator passwordData={passStrData} />
                    </div>
                    <div
                        style={{ maxWidth: '400px' }}
                        className='d-grid gap-2 mt-4 d-flex justify-content-center gap-4 mt-5 mx-auto'
                    >
                        <button
                            type='submit'
                            className='btn btn-splendi-blue w-50 text-center fw-bold'
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
                {isLoading ? (
                    <div className='d-flex justify-content-center mt-5'>
                        <span
                            style={{ width: '50px', height: '50px' }}
                            className='spinner-border spinner-border-sm'
                        ></span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default Password;
